import { Card } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { ResourceFileItem } from "./resource-file-item"

interface File {
  name: string
  size: string
  type: "pdf" | "docx" | "video"
}

interface ResourceCardProps {
  title: string
  description: string
  subject: string
  assignedDate: string
  emoji: string
  files: File[]
  status: "completed" | "in-progress" | "not-started"
  progress?: number
  collaborators?: string[]
}

export function ResourceCard({
  title,
  description,
  subject,
  assignedDate,
  emoji,
  files,
  status,
  progress,
  collaborators,
}: ResourceCardProps) {
  const getStatusBadge = () => {
    switch (status) {
      case "completed":
        return <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Completed</Badge>
      case "in-progress":
        return <Badge className="bg-yellow-100 text-yellow-700 hover:bg-yellow-100">In Progress</Badge>
      case "not-started":
        return <Badge className="bg-gray-100 text-gray-700 hover:bg-gray-100">Not Started</Badge>
    }
  }

  return (
    <Card className="p-6 hover:shadow-lg transition-all duration-300 bg-slate-100 hover:-translate-y-1 overflow-hidden relative">
      {/* Decorative background elements */}
      <div className="absolute -top-8 -right-8 w-32 h-32 bg-primary/5 rounded-full opacity-50 pointer-events-none" />
      <div className="absolute top-10 right-0 w-24 h-24 bg-accent/10 rounded-full opacity-50 pointer-events-none" />

      <div className="relative z-10 bg-slate-100">
        {/* Header */}
        <div className="flex justify-between items-start mb-4">
          <div className="flex-1">
            <h3 className="text-xl font-bold text-gray-800">{title}</h3>
            <p className="text-sm text-gray-800 mt-1">{description}</p>
          </div>
          <span className="text-4xl animate-bounce">{emoji}</span>
        </div>

        {/* Meta info */}
        <div className="flex items-center text-sm text-gray-800 space-x-4 mb-6">
          <span>
            <span className="font-semibold">Subject:</span> {subject}
          </span>
          <span>
            <span className="font-semibold">Assigned:</span> {assignedDate}
          </span>
        </div>

        {/* Files */}
        <div className="space-y-3 mb-6 text-gray-800">
          {files.map((file, idx) => (
            <ResourceFileItem key={idx} {...file} />
          ))}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            {getStatusBadge()}
            {progress !== undefined && status === "in-progress" && (
              <div className="flex items-center gap-2">
                <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-accent transition-all" style={{ width: `${progress}%` }} />
                </div>
                <span className="text-xs text-muted-foreground">{progress}%</span>
              </div>
            )}
          </div>
          {/* {collaborators && collaborators.length > 0 && (
            <div className="flex -space-x-2">
              {collaborators.map((avatar, idx) => (
                <img
                  key={idx}
                  src={avatar || "/placeholder.svg"}
                  alt="Collaborator"
                  className="w-8 h-8 rounded-full ring-2 ring-background"
                />
              ))}
            </div>
          )} */}
        </div>
      </div>
    </Card>
  )
}
